import { z } from 'zod';
import { USER_ROLES } from '../constants.js';
/**
 * Provider capacity (v0.11 design, item 15): limits OCI keeps below a model
 * provider's own rate limits, shared by every API replica, and the queue a
 * turn waits in when they are reached.
 */
export const MAX_REQUESTS_PER_MINUTE = 1_000_000;
export const MAX_TOKENS_PER_MINUTE = 1_000_000_000;
export const MAX_CONCURRENT_STREAMS = 100_000;
export const DEFAULT_MAX_QUEUE_WAIT_SECONDS = 120;
export const MIN_QUEUE_WAIT_SECONDS = 5;
export const MAX_QUEUE_WAIT_SECONDS = 1_800;
const limit = (max, what) => z
    .number()
    .int(`${what} must be a whole number.`)
    .positive(`${what} must be at least 1, or empty for no limit.`)
    .max(max, `${what} can be at most ${max.toLocaleString('en-US')}.`)
    .nullable();
/** One provider's or model's limits. Null (or absent) means no limit. */
export const capacityLimitsSchema = z.object({
    requestsPerMinute: limit(MAX_REQUESTS_PER_MINUTE, 'Requests per minute').default(null),
    tokensPerMinute: limit(MAX_TOKENS_PER_MINUTE, 'Tokens per minute').default(null),
    maxConcurrentStreams: limit(MAX_CONCURRENT_STREAMS, 'Concurrent streams').default(null),
});
export const NO_CAPACITY_LIMITS = {
    requestsPerMinute: null,
    tokensPerMinute: null,
    maxConcurrentStreams: null,
};
export function hasCapacityLimits(limits) {
    return Boolean(limits &&
        (limits.requestsPerMinute !== null ||
            limits.tokensPerMinute !== null ||
            limits.maxConcurrentStreams !== null));
}
/**
 * Queue order by role. `high` starts a minute ahead of `normal`, `low` a
 * minute behind: a turn never waits behind a higher role forever, only until
 * it has waited a minute longer than the newcomer.
 */
export const QUEUE_PRIORITIES = ['high', 'normal', 'low'];
export const capacityQueueSettingsSchema = z.object({
    maxWaitSeconds: z
        .number()
        .int('Maximum wait must be a whole number of seconds.')
        .min(MIN_QUEUE_WAIT_SECONDS, `Maximum wait must be at least ${MIN_QUEUE_WAIT_SECONDS} seconds.`)
        .max(MAX_QUEUE_WAIT_SECONDS, `Maximum wait can be at most ${MAX_QUEUE_WAIT_SECONDS} seconds.`),
    rolePriority: z.record(z.enum(USER_ROLES), z.enum(QUEUE_PRIORITIES)),
});
export const updateCapacityQueueSchema = z.object({
    maxWaitSeconds: capacityQueueSettingsSchema.shape.maxWaitSeconds.optional(),
    rolePriority: z.partialRecord(z.enum(USER_ROLES), z.enum(QUEUE_PRIORITIES)).optional(),
});
export const DEFAULT_CAPACITY_QUEUE_SETTINGS = {
    maxWaitSeconds: DEFAULT_MAX_QUEUE_WAIT_SECONDS,
    rolePriority: { admin: 'normal', auditor: 'normal', user: 'normal', restricted: 'normal' },
};
/**
 * The reply part written while a turn waits for capacity (`data-capacity`,
 * id `capacity`), updated in place.
 *
 * - `waiting`: `position` is 1 for the next turn to start.
 * - `admitted`: the wait is over and the reply starts.
 * - `timeout`: the wait passed the maximum; the reply failed.
 * - `handoff`: the server restarted before the reply started; the browser
 *   sends the message again (to a server that is running) by itself.
 */
export const CAPACITY_PART_TYPE = 'data-capacity';
export const CAPACITY_PART_ID = 'capacity';
/** The capacity part's data, when a message part is one. */
export function capacityWaitOfPart(part) {
    if (typeof part !== 'object' || part === null)
        return null;
    const candidate = part;
    if (candidate.type !== CAPACITY_PART_TYPE)
        return null;
    const data = candidate.data;
    if (!data ||
        typeof data !== 'object' ||
        !['waiting', 'admitted', 'timeout', 'handoff'].includes(data.state))
        return null;
    const count = (value) => typeof value === 'number' && Number.isFinite(value) && value >= 0 ? value : null;
    return {
        state: data.state,
        model: typeof data.model === 'string' ? data.model : '',
        position: count(data.position),
        estimatedWaitSeconds: count(data.estimatedWaitSeconds),
        waitedSeconds: count(data.waitedSeconds) ?? 0,
    };
}
//# sourceMappingURL=capacity.js.map